import { regionAt, type Region } from '../../shared/region.js';
import { el } from './dom.js';

/**
 * The street-name plate that slides in when the car turns onto a new street, in the style
 * the street's own country puts on its corners.
 *
 * It is the cheapest thing in the game that says *where* you are rather than *how fast*:
 * the name of the street you just turned into, on a sign that looks like the ones on the
 * buildings. A blue Polish plate with its small "ul.", a green American one, Paris's blue
 * enamel in its green frame. See DECISIONS.md §71.
 */

/**
 * Which country's plate. The same outlines decide which country's buildings the city is
 * drawn with, so they live in `shared/region.ts`, where the world generator can reach them.
 */
export type SignStyle = Region;
export const signStyleAt = regionAt;

/** What goes on the plate: a small word above or before the name, the name, a small word after. */
export interface SignParts {
  prefix: string;
  main: string;
  suffix: string;
}

/**
 * A Polish plate says what kind of street it is in small letters above the name — "ul."
 * for nearly all of them — and OSM leaves "ulica" out of the name while keeping "Aleja"
 * and "Plac" in it. So the prefix is inferred: the street's own kind word, shortened the
 * way the plates shorten it, or "ul." when there is none. Names that are a place rather
 * than a street — Rynek Główny, Planty — get no prefix at all.
 */
const PL_KINDS: Record<string, string> = {
  aleja: 'al.',
  aleje: 'al.',
  'al.': 'al.',
  plac: 'pl.',
  'pl.': 'pl.',
  rondo: 'rondo',
  most: 'most',
  bulwar: 'bulw.',
  osiedle: 'os.',
  'os.': 'os.',
  skwer: 'skwer',
  ulica: 'ul.',
  'ul.': 'ul.',
};
const PL_PLACES = new Set(['rynek', 'mały', 'planty', 'wybrzeże', 'park', 'droga', 'trasa', 'zaułek', 'kopiec']);

/** American plates abbreviate the street type and the compass word: "W 45th St". */
const US_SUFFIX: Record<string, string> = {
  street: 'St',
  avenue: 'Ave',
  boulevard: 'Blvd',
  road: 'Rd',
  drive: 'Dr',
  place: 'Pl',
  lane: 'Ln',
  court: 'Ct',
  parkway: 'Pkwy',
  highway: 'Hwy',
  terrace: 'Ter',
  square: 'Sq',
  plaza: 'Plz',
  expressway: 'Expy',
};
const US_COMPASS: Record<string, string> = { east: 'E', west: 'W', north: 'N', south: 'S' };

export function signParts(name: string, style: SignStyle): SignParts {
  const words = name.trim().split(/\s+/);
  const first = words[0]?.toLocaleLowerCase('pl') ?? '';
  switch (style) {
    case 'pl': {
      if (PL_PLACES.has(first)) return { prefix: '', main: name, suffix: '' };
      const kind = PL_KINDS[first];
      if (kind && words.length > 1) return { prefix: kind, main: words.slice(1).join(' '), suffix: '' };
      return { prefix: 'ul.', main: name, suffix: '' };
    }
    case 'us': {
      if (words.length < 2) return { prefix: '', main: name, suffix: '' };
      const last = words[words.length - 1].toLowerCase();
      const suffix = US_SUFFIX[last] ?? '';
      const body = suffix ? words.slice(0, -1) : words.slice();
      // "Avenue of the Americas" and "South Street" keep their first word: it is the name.
      if (body.length > 1 && US_COMPASS[body[0].toLowerCase()]) body[0] = US_COMPASS[body[0].toLowerCase()];
      return { prefix: '', main: body.join(' '), suffix };
    }
    default:
      return { prefix: '', main: name, suffix: '' };
  }
}

/** On screen this long, entrance and exit included. Matches `street-sign-in` in the CSS. */
const SHOW_MS = 2800;

export class StreetSign {
  readonly root: HTMLElement;
  private readonly prefix: HTMLElement;
  private readonly main: HTMLElement;
  private readonly suffix: HTMLElement;
  private hideTimer: number | null = null;

  constructor() {
    this.prefix = el('span', { class: 'street-sign-prefix' });
    this.main = el('span', { class: 'street-sign-name' });
    this.suffix = el('span', { class: 'street-sign-suffix' });
    // Hidden from screen readers: a name every few seconds read over the race would be
    // noise, and nothing in it is needed to play.
    this.root = el(
      'div',
      { class: 'street-sign', dataset: { style: 'plain' } },
      el(
        'div',
        { class: 'street-sign-plate' },
        this.prefix,
        el('span', { class: 'street-sign-line' }, this.main, this.suffix),
      ),
    );
    this.root.setAttribute('aria-hidden', 'true');
  }

  setStyle(style: SignStyle): void {
    this.root.dataset.style = style;
  }

  show(name: string): void {
    const parts = signParts(name, (this.root.dataset.style ?? 'plain') as SignStyle);
    this.prefix.textContent = parts.prefix;
    this.main.textContent = parts.main;
    this.suffix.textContent = parts.suffix;
    // Restarted rather than left running, so a second street inside the first's three
    // seconds slides in fresh instead of inheriting the fade of the one it replaced.
    this.root.classList.remove('is-showing');
    void this.root.offsetWidth;
    this.root.classList.add('is-showing');
    if (this.hideTimer !== null) window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => this.hide(), SHOW_MS);
  }

  hide(): void {
    if (this.hideTimer !== null) window.clearTimeout(this.hideTimer);
    this.hideTimer = null;
    this.root.classList.remove('is-showing');
  }
}
